import {
  Component, Input, Output, EventEmitter, SimpleChanges, OnChanges, ViewEncapsulation, OnInit, OnDestroy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { isNil, isObject } from 'lodash';
import {
  BehaviorSubject, filter, firstValueFrom, map, Observable, Subject, takeUntil,
} from 'rxjs';
import { OrganizationUnitService } from 'src/api/customer/organization-unit/organization-unit.service';
import { PermissionProvider } from 'src/providers/permission.provider';
import { DoenkidsSessionProvider } from 'src/providers/session.provider';
import { IOrganizationUnit, IOrganizationUnitOverview } from 'typings/doenkids/doenkids';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from 'src/app/utils/translate.service';
import { OrganizationUnitTypeNamePipe } from 'src/pipes/organization-unit-type-name';
import { I18nToastProvider } from 'src/providers/i18n-toast.provider';
import { DoenkidsStaticValuesHelper } from 'src/components/shared/static-values/doenkids-static-values-helper';
import { ConfirmationDialogComponent } from '../dialogs/confirmation-dialog/confirmation-dialog.component';

interface OwnerChipOrganizationUnitRole {
  organization_unit_id: number;
  organization_unit_role_id?: number;
  organization_unit_role_name?: string;
}

type OwnerChipOrganizationUnit = IOrganizationUnit & {
  roleId?: number;
  roleDisplayName?: string;
  roleIcon?: string;
  roleStyleClass?: string;
};

@Component({
    selector: 'app-owner-chip',
    templateUrl: './owner-chip.component.html',
    styleUrls: ['./owner-chip.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class OwnerChipComponent implements OnInit, OnChanges, OnDestroy {
  private stop$ = new Subject<void>();

  @Input() owningOuId: number;

  @Input() owningOuIds: number[];

  @Input() owningOuRoles: OwnerChipOrganizationUnitRole[];

  @Input() isTheOwner;

  @Output() isTheOwnerChange = new EventEmitter<boolean>();

  public isAdmin$: Observable<boolean>;

  public owner$ = new BehaviorSubject<OwnerChipOrganizationUnit>(null);

  public ownerList$ = new BehaviorSubject<OwnerChipOrganizationUnit[]>([]);

  public extraOwners$: Observable<OwnerChipOrganizationUnit[]> = this.ownerList$.pipe(
    map((list) => list.slice(1)),
  );

  public hasOwnerWritePermission$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$: Observable<boolean>;

  constructor(
    private $organizationUnit: OrganizationUnitService,
    private $session: DoenkidsSessionProvider,
    private $permission: PermissionProvider,
    private dialog: MatDialog,
    private $translateService: TranslateService,
    private organizationUnitTypeNamePipe: OrganizationUnitTypeNamePipe,
    private $i18nToastProvider: I18nToastProvider,
  ) {
    this.isAdmin$ = this.$session.isAdmin$;

    this.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$ = this.$permission.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$.asObservable();

    this.$session.getOrganizationUnit$.pipe(
      takeUntil(this.stop$),
    ).subscribe((organization) => {
      this.checkOwnership(organization);
    });

    this.owner$.pipe(
      takeUntil(this.stop$),
      filter((owner) => !isNil(owner)),
    ).subscribe(async (owner) => {
      if (owner) {
        const hasWritePermissionsOnOwner = await this.$permission.checkOUWritePermission(owner.id);
        this.hasOwnerWritePermission$.next(hasWritePermissionsOnOwner);
      }
    });
  }

  ngOnInit() {
    this.checkOwnership(this.$session.getCurrentOu());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.organizationUnitId && !changes.organizationUnitId.firstChange) {
      this.checkOwnership(this.$session.getCurrentOu());
    }

    if (changes.owningOuId && !changes.owningOuId.firstChange) {
      this.checkOwnership(this.$session.getCurrentOu());
    }

    if (changes.owningOuIds && !changes.owningOuIds.firstChange) {
      this.checkOwnership(this.$session.getCurrentOu());
    }

    if (changes.owningOuRoles && !changes.owningOuRoles.firstChange) {
      this.checkOwnership(this.$session.getCurrentOu());
    }
  }

  ngOnDestroy(): void {
    this.stop$.next();
  }

  async checkOwnership(currentOrganization: IOrganizationUnitOverview) {
    const roleByOrganizationUnitId = new Map(
      (this.owningOuRoles ?? []).map((role) => [role.organization_unit_id, role]),
    );
    const effectiveIds: number[] = (
      this.owningOuIds?.length
        ? this.owningOuIds
        : (this.owningOuRoles?.length
          ? this.owningOuRoles.map((role) => role.organization_unit_id)
          : (this.owningOuId ? [this.owningOuId] : []))
    ).filter((id) => !isNil(id));

    if (!effectiveIds.length) { return; }

    const owners = await Promise.all(effectiveIds.map((id) => this.$organizationUnit.fetch(id)));
    const validOwners = owners
      .filter((ou) => !isNil(ou))
      .map((ou) => ({
        ...ou,
        roleId: roleByOrganizationUnitId.get(ou.id)?.organization_unit_role_id,
        roleDisplayName: this.getRoleDisplayName(roleByOrganizationUnitId.get(ou.id)),
        roleIcon: this.getRoleIcon(roleByOrganizationUnitId.get(ou.id)),
        roleStyleClass: this.getRoleStyleClass(roleByOrganizationUnitId.get(ou.id)),
      }));

    const sortedOwners = [...validOwners].sort((a, b) => {
      const aIsOwner = a.roleId === DoenkidsStaticValuesHelper.ACTIVITY_OWNER_ROLE ? 0 : 1;
      const bIsOwner = b.roleId === DoenkidsStaticValuesHelper.ACTIVITY_OWNER_ROLE ? 0 : 1;
      if (aIsOwner !== bIsOwner) { return aIsOwner - bIsOwner; }
      return (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });
    });
    const sortedActivityOwners = sortedOwners.filter(
      (ou) => ou.roleId === DoenkidsStaticValuesHelper.ACTIVITY_OWNER_ROLE,
    );

    const currentOuAssignment = currentOrganization
      ? sortedOwners.find((ou) => ou.id === currentOrganization.id)
      : undefined;
    const currentOuReaderOrOwner = currentOuAssignment && [
      DoenkidsStaticValuesHelper.ACTIVITY_OWNER_ROLE,
      DoenkidsStaticValuesHelper.ACTIVITY_READER_ROLE,
    ].includes(currentOuAssignment.roleId);

    const fallbackOwner = sortedActivityOwners[0] ?? sortedOwners[0] ?? null;
    const primaryOwner = currentOuReaderOrOwner
      ? currentOuAssignment
      : (fallbackOwner
        ? {
          ...fallbackOwner,
          roleDisplayName: '',
          roleIcon: 'lock',
          roleStyleClass: 'owner-chip-unassigned-owner',
        }
        : null);

    this.owner$.next(primaryOwner);
    this.ownerList$.next(sortedOwners);

    const newIsOwner = (primaryOwner && currentOrganization) ? primaryOwner.id === currentOrganization.id : false;

    if (newIsOwner !== this.isTheOwner) {
      this.isTheOwner = newIsOwner;
      this.isTheOwnerChange.emit(this.isTheOwner);
    }
  }

  private getRoleDisplayName(role?: OwnerChipOrganizationUnitRole): string {
    switch (role?.organization_unit_role_id) {
      case DoenkidsStaticValuesHelper.ACTIVITY_READER_ROLE:
        return this.$translateService.instant(_('owner_chip.role.reader'));
      case DoenkidsStaticValuesHelper.ACTIVITY_EDITOR_ROLE:
        return this.$translateService.instant(_('owner_chip.role.contributor'));
      case DoenkidsStaticValuesHelper.ACTIVITY_OWNER_ROLE:
        return this.$translateService.instant(_('owner_chip.role.owner'));
      default:
        return role?.organization_unit_role_name ?? '';
    }
  }

  private getRoleIcon(role?: OwnerChipOrganizationUnitRole): string {
    switch (role?.organization_unit_role_id) {
      case DoenkidsStaticValuesHelper.ACTIVITY_READER_ROLE:
        return 'visibility';
      case DoenkidsStaticValuesHelper.ACTIVITY_EDITOR_ROLE:
        return 'edit';
      case DoenkidsStaticValuesHelper.ACTIVITY_OWNER_ROLE:
      default:
        return 'vpn_key';
    }
  }

  private getRoleStyleClass(role?: OwnerChipOrganizationUnitRole): string {
    switch (role?.organization_unit_role_id) {
      case DoenkidsStaticValuesHelper.ACTIVITY_READER_ROLE:
        return 'owner-chip-reader';
      case DoenkidsStaticValuesHelper.ACTIVITY_EDITOR_ROLE:
        return 'owner-chip-contributor';
      case DoenkidsStaticValuesHelper.ACTIVITY_OWNER_ROLE:
      default:
        return 'owner-chip-owner';
    }
  }

  async switchToOwner() {
    const currentOwner = await firstValueFrom(this.owner$);
    if (!isObject(currentOwner)) {
      return;
    }

    this.switchToOrganizationUnit(currentOwner);
  }

  async switchToOrganizationUnit(organizationUnit: OwnerChipOrganizationUnit) {
    if (!isObject(organizationUnit)) {
      return;
    }

    const organizationUnitTypeName = this.$translateService.instant(
      this.organizationUnitTypeNamePipe.transform(organizationUnit.organization_unit_type_id),
    );
    const hasWritePermission = await this.$permission.checkOUWritePermission(organizationUnit.id);
    if (!hasWritePermission) {
      this.$i18nToastProvider.error(
        _('owner_chip.switch.unauthorized'),
        {
          organizationUnitTypeName,
          organizationUnitName: organizationUnit.name,
        },
      );

      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      minWidth: '320px',
      data: {
        title: this.$translateService.instant(
          _('owner_chip.switch.dialog.title'),
          {
            organizationUnitTypeName,
            organizationUnitName: organizationUnit.name,
          },
        ),
        description: this.$translateService.instant(_('owner_chip.switch.dialog.description'), { organizationUnitTypeName }),
      },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result === 'confirm') {
      this.$session.selectOrganizationUnit(organizationUnit.id);
    }
  }
}
